import {
  type AirCfarOffer,
  GetAirCfarOfferResponseEnum,
  type TrackingProperties,
  type AirCfarOfferV2,
  type FareRefundabilityV2 as FareRefundability,
  type GetAirCfarOffersRequestV2,
} from "@b2bportal/air-cfar-api";
import {
  CallState,
  type SelectedCfarOfferStatus,
  SelectedStatus,
} from "@b2bportal/core-types";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";

import { fetchCfarOffers, fetchCfarOffersV2 } from "../thunks";

export interface CfarOffersState {
  fetchCfarOffersRequest: GetAirCfarOffersRequestV2 | undefined;
  fetchCfarOffersCallState: CallState;
  cfarOffers: AirCfarOffer[] | AirCfarOfferV2[];
  selectedCfarOffer: SelectedCfarOfferStatus;
  skipCfarTrackingProperties?: TrackingProperties;
  fareRefundability: FareRefundability | undefined;
  fetchCfarOfferTrackingProperties: TrackingProperties | undefined;
}

export const initialCfarOffersState: CfarOffersState = {
  fetchCfarOffersRequest: undefined,
  fetchCfarOffersCallState: CallState.NotCalled,
  cfarOffers: [],
  selectedCfarOffer: { status: SelectedStatus.UNSELECTED },
  skipCfarTrackingProperties: undefined,
  fareRefundability: undefined,
  fetchCfarOfferTrackingProperties: undefined,
};

export const slice = createSlice({
  name: "flightsCfarOffers",
  initialState: initialCfarOffersState,
  reducers: {
    setSelectedCfarOffer: (
      state,
      action: PayloadAction<SelectedCfarOfferStatus>
    ) => {
      state.selectedCfarOffer = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCfarOffers.pending, (state, _) => {
        state.fetchCfarOffersCallState = CallState.InProcess;
      })
      .addCase(fetchCfarOffers.rejected, (state, _) => {
        state.fetchCfarOffersCallState = CallState.Failed;
      })
      .addCase(fetchCfarOffers.fulfilled, (state, action) => {
        state.fetchCfarOffersCallState = CallState.Success;
        const response = action.payload;
        const offerResponse = response.GetAirCfarOfferResponse;
        switch (offerResponse) {
          case GetAirCfarOfferResponseEnum.Available:
            state.cfarOffers = response.offers;
            // reset selected offer if the user navigates back and forth
            state.selectedCfarOffer = { status: SelectedStatus.UNSELECTED };
            return;
          case GetAirCfarOfferResponseEnum.NotAvailable:
            state.cfarOffers = [];
            state.skipCfarTrackingProperties = response.trackingProperties;
            // reset selected offer if the user navigates back and forth
            state.selectedCfarOffer = { status: SelectedStatus.UNSELECTED };
            return;
          case GetAirCfarOfferResponseEnum.InvalidOffer:
            state.cfarOffers = [];
            // reset selected offer if the user navigates back and forth
            state.selectedCfarOffer = { status: SelectedStatus.UNSELECTED };
            return;
          default:
            return ensureExhaustive(offerResponse);
        }
      })
      .addCase(fetchCfarOffersV2.pending, (state, action) => {
        state.fetchCfarOffersRequest = action.meta.arg;
        state.fetchCfarOffersCallState = CallState.InProcess;
      })
      .addCase(fetchCfarOffersV2.rejected, (state, _) => {
        state.fetchCfarOffersCallState = CallState.Failed;
      })
      .addCase(fetchCfarOffersV2.fulfilled, (state, action) => {
        state.fetchCfarOffersCallState = CallState.Success;
        const response = action.payload;
        state.cfarOffers = response.offers;
        state.fareRefundability = response.fareRefundability;
        state.fetchCfarOfferTrackingProperties = response.trackingProperties;

        state.selectedCfarOffer = { status: SelectedStatus.UNSELECTED };
      });
  },
});

export const FlightsCfarOffersActions = slice.actions;
